import { RouteLocationNormalized } from "vue-router"
import { useAcl } from "vue-simple-acl"

import { User } from "@/models"
import { $keycloak } from "@/plugins/keycloak"
import { Actions, Getters, store } from "@/store"
import { isTempAccountName, normalizeParam } from "@/utils"

export default async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: (...args) => void
): Promise<void> => {
  if (to.name === "ServiceUnavailable") {
    next()
    return
  }

  const auth = $keycloak.authenticated

  if (!auth) {
    next()
    return
  }
  try {
    const { params, path } = to
    let currentProfile = store.getters[Getters.GET_USER_CURRENT_PROFILE]

    if (!currentProfile?.id) {
      currentProfile = await store.dispatch(Actions.GET_USER_CURRENT_PROFILE)
      if (!currentProfile?.id) {
        return next(false)
      }
    }
    if (!currentProfile?.emailVerified && to.name !== "VerificationEmail") {
      next("/email-verification")
      return
    }

    if (to.meta?.withoutAccount || to.meta?.adminPage) {
      return next()
    }

    const currentAccountName = store.getters[Getters.GET_CURRENT_ACCOUNT_NAME]

    if (!currentAccountName && useAcl().can("superAdmin")) {
      const routeRequiresAccountId = to.matched.some(route => route.path.includes("/:accountId"))
      const accountId = normalizeParam(params.accountId)
      const hasInvalidAccountId = !accountId || accountId === "undefined"

      return routeRequiresAccountId && hasInvalidAccountId ? next("/accounts") : next()
    }

    const hasVerifiedPayment = store.getters[Getters.GET_CURRENT_ACCOUNT_HAS_PAYMENT_VERIFIED]
    const currentAccountId = store.getters[Getters.GET_CURRENT_ACCOUNT_ID]

    const accountId = normalizeParam(params.accountId)
    const isTemp = isTempAccountName(currentAccountName)

    if (isTemp) {
      if (!hasVerifiedPayment) {
        return path === "/subscription" || path === "/redeem"
          ? next()
          : next({ path: "/subscription" })
      } else {
        return path === "/company/create" ? next() : next({ path: "/company/create" })
      }
    }

    if (await isAccountSetupInProgress(currentAccountId, currentProfile)) {
      return path === "/company/create" ? next() : next({ path: "/company/create" })
    }

    if (!accountId) {
      if (!currentAccountId) {
        return path === "/subscription" || path === "/redeem"
          ? next()
          : next({
              path: "/subscription"
            })
      }

      const accountUrlName = findNameById(currentAccountId)
      return next(accountUrlName ? `/${accountUrlName}` : false)
    }

    if (!currentAccountName) {
      path === "/redeem"
        ? next()
        : next({
            path: "/subscription"
          })
    }

    if ((!currentProfile.firstName || !currentProfile.lastName) && to.name !== "InviteUser") {
      return next({
        name: "InviteUser"
      })
    }

    if (path === "/projects/projects" && currentAccountName !== "projects") {
      return next("/")
    }

    next()
  } catch (error) {
    if (error instanceof Error && error.message === "Service unavailable") {
      return
    }

    console.error("Error in currentUser middleware:", error)
    next(false)
  }
}

/**
 * Tells whether the account admin still has an unfinished sign-up and must be returned to the wizard.
 *
 * Only admins are checked, so invited members never land in the owner's wizard. A failed request lets
 * the navigation through, and the next navigation checks again because failures are not cached.
 */
async function isAccountSetupInProgress(accountId: string, profile: User): Promise<boolean> {
  if (!accountId || !profile.isAdmin(accountId)) return false
  const setupProgress = await store.dispatch(Actions.GET_ACCOUNT_SETUP_PROGRESS, accountId)
  return setupProgress ? !setupProgress.setupCompleted : false
}

function findNameById(id: string) {
  const accounts = store.getters[Getters.GET_USER_CURRENT_PROFILE].accounts
  if (accounts && accounts.length) {
    return store.getters[Getters.GET_USER_CURRENT_PROFILE].accounts.find(a => a.id === id).urlName
  }
}
